/*
  Filters the vehicle finder's product grid in the browser.

  Liquid cannot query products by metafield - the relationship only runs
  product -> vehicle, through custom.compatible_vehicles - so there is no
  server-side way to ask "which products fit this vehicle". The section renders
  every product once with its vehicle handles in data-vehicles and this narrows
  the grid by hiding the rest.

  Choosing a make filters to everything fitting any vehicle of that make, so the
  grid is useful after one tap rather than two. Choosing a model then narrows it.

  A custom element rather than a DOMContentLoaded pass, because the theme editor
  replaces the whole section on every settings change and connectedCallback
  re-runs on the replacement for free.
*/

class VehicleFinder extends HTMLElement {
  connectedCallback() {
    this.picker = this.querySelector('[data-picker]');
    this.results = this.querySelector('[data-results]');
    if (!this.picker || !this.results) return;

    this.modelsStep = this.querySelector('[data-models-step]');
    this.summary = this.querySelector('[data-summary]');
    this.resetButton = this.querySelector('[data-reset]');

    this.makeButtons = Array.from(this.querySelectorAll('.vehicle-finder__make'));
    this.modelItems = Array.from(this.querySelectorAll('.vehicle-finder__models > li'));
    this.modelButtons = Array.from(this.querySelectorAll('.vehicle-finder__model'));

    // Parsed once - the grid is filtered on every tap and re-splitting the
    // attribute each time is pure waste.
    this.cards = Array.from(this.results.querySelectorAll('[data-vehicles]')).map((element) => ({
      element,
      vehicles: new Set((element.dataset.vehicles || '').split(' ').filter(Boolean)),
    }));

    this.addEventListener('click', this.onClick.bind(this));

    // Liquid ships the picker hidden so that a script that never loads degrades
    // to a plain product grid instead of to controls that do nothing.
    this.picker.hidden = false;
    this.reset();
  }

  onClick(event) {
    const make = event.target.closest('.vehicle-finder__make');
    if (make) {
      this.selectMake(make);
      return;
    }

    const model = event.target.closest('.vehicle-finder__model');
    if (model) {
      this.selectModel(model);
      return;
    }

    if (event.target.closest('[data-reset]')) this.reset();
  }

  selectMake(button) {
    const make = button.dataset.make;

    this.makeButtons.forEach((element) => {
      element.setAttribute('aria-pressed', String(element === button));
    });
    this.modelButtons.forEach((element) => element.setAttribute('aria-pressed', 'false'));

    const items = this.modelItems.filter((item) => item.dataset.make === make);
    this.modelItems.forEach((item) => {
      item.hidden = item.dataset.make !== make;
    });
    if (this.modelsStep) this.modelsStep.hidden = items.length === 0;

    const handles = items
      .map((item) => {
        const model = item.querySelector('.vehicle-finder__model');
        return model ? model.dataset.vehicle : null;
      })
      .filter(Boolean);

    this.filter(handles, this.labelOf(button));
  }

  selectModel(button) {
    this.modelButtons.forEach((element) => {
      element.setAttribute('aria-pressed', String(element === button));
    });

    this.filter([button.dataset.vehicle], this.labelOf(button));
  }

  filter(handles, label) {
    const wanted = handles;
    let matches = 0;

    this.cards.forEach((card) => {
      const hit = wanted.some((handle) => card.vehicles.has(handle));
      card.element.hidden = !hit;
      if (hit) matches += 1;
    });

    if (this.resetButton) this.resetButton.hidden = false;
    this.announce(matches, label);
  }

  reset() {
    this.makeButtons.forEach((element) => element.setAttribute('aria-pressed', 'false'));
    this.modelButtons.forEach((element) => element.setAttribute('aria-pressed', 'false'));
    this.modelItems.forEach((item) => {
      item.hidden = true;
    });
    this.cards.forEach((card) => {
      card.element.hidden = false;
    });

    if (this.modelsStep) this.modelsStep.hidden = true;
    if (this.resetButton) this.resetButton.hidden = true;
    if (this.summary) this.summary.textContent = '';
  }

  // The make button carries a logo as well as its name, so read the name span
  // rather than the button's whole text content.
  labelOf(button) {
    const name = button.querySelector('.vehicle-finder__make-name');
    return (name ? name.textContent : button.textContent).trim();
  }

  announce(matches, label) {
    if (!this.summary) return;

    if (matches === 0) {
      this.summary.textContent = this.dataset.emptyText || '';
      return;
    }

    const template =
      matches === 1
        ? this.dataset.resultsTextSingular || '[count] product for [vehicle]'
        : this.dataset.resultsText || '[count] products for [vehicle]';

    this.summary.textContent = template
      .replace('[count]', String(matches))
      .replace('[vehicle]', label);
  }
}

customElements.define('vehicle-finder', VehicleFinder);
